import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  LocalPrintshop as ThermalPrinterIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Done as DoneIcon,
  Settings as SettingsIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import AkMoInvoicingFormat from '../templates/AkMoInvoicingFormat';
import ZohoAkMoA4Template from '../templates/ZohoAkMoA4Template';
import ThermalInvoiceFormat from '../templates/ThermalInvoiceFormat';
import ThermalInvoiceFormatRegular from '../templates/ThermalInvoiceFormatRegular';
import ThermalInvoiceFormatUnregistered from '../templates/ThermalInvoiceFormatUnregistered';
import ThermalInvoiceFormatComposition from '../templates/ThermalInvoiceFormatComposition';
import { generateInvoicePDF } from '../../utils/invoicePdfGenerator';
import { exportInvoiceToPdf } from '../../utils/invoicePdfExporter';
import { InvoicePrinter } from '../../utils/invoicePrinter';
import { EmailService } from '../../utils/emailService';
import { invoiceTemplateOptions } from '../../utils/invoiceTemplates';
import { useAuth } from '../../context/AuthContext';
import SettingsService, { BusinessProfileSettings } from '../../services/settingsService';

// Define interfaces
interface InvoiceItem {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
  taxAmount?: number;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };
  deliveryDetails?: {
    name: string;
    address: string;
    city?: string;
    state?: string;
    pincode?: string;
    phone?: string;
    email?: string;
    contactPerson?: string;
    contactNumber?: string;
  };
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;
  userId: string;
  createdAt: string;
  additionalCharges?: {
    id: number;
    name: string;
    amount: number;
    taxable?: boolean;
  }[];
}

interface InvoicePreviewStepProps {
  invoiceData: InvoiceData;
  businessDetails: any;
  onPrint?: () => void;
  onThermalPrint?: () => void;
  onEmail?: () => void;
  onSavePdf?: () => void;
  onCreateNextInvoice?: () => void;
  /** When true hides the built-in action buttons and Card wrapper (used when embedded inline) */
  hideActionButtons?: boolean;
}

const InvoicePreviewStep: React.FC<InvoicePreviewStepProps> = ({
  invoiceData,
  businessDetails,
  onPrint,
  onThermalPrint,
  onEmail,
  onSavePdf,
  onCreateNextInvoice,
  hideActionButtons = false,
}) => {
  // Format currency - moved to the top before it's used
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };
  
  const [showSettings, setShowSettings] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState(invoiceData.clientDetails.email || '');
  const [emailSubject, setEmailSubject] = useState(`Invoice ${invoiceData.invoiceNumber} from ${businessDetails?.name || 'Business'}`);
  const [emailBody, setEmailBody] = useState(
    `Dear ${invoiceData.client},\n\nPlease find attached the invoice ${invoiceData.invoiceNumber} for your recent purchase.\n\nTotal Amount: ${formatCurrency(invoiceData.amount)}\nDue Date: ${invoiceData.dueDate}\n\nThank you for your business.\n\nRegards,\n${businessDetails?.name || ''}`);
  const [showThermalPreview, setShowThermalPreview] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const thermalRef = useRef<HTMLDivElement>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [thermalWidth, setThermalWidth] = useState<'3-inch' | '2-inch'>('3-inch');
  
  const { currentUser } = useAuth();
  const [brandSettings, setBrandSettings] = useState<BusinessProfileSettings | null>(null);
  const [userSmtpSettings, setUserSmtpSettings] = useState<any>(null);
  const [userInvoiceSettings, setUserInvoiceSettings] = useState<any>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      if (currentUser) {
        const settings = await SettingsService.fetchSettings(currentUser.uid);
        if (settings?.businessProfile) {
          setBrandSettings(settings.businessProfile);
        }
        if (settings?.smtpSettings) {
          setUserSmtpSettings(settings.smtpSettings);
        }
        if (settings?.invoiceSettings) {
          setUserInvoiceSettings(settings.invoiceSettings);
        }
      }
    };
    fetchSettings();
  }, [currentUser]);
  
  // Always use AkMo Invoice template (ID: 1)
  const templateId = 1;
  
  // Get the AkMo template
  const akmoTemplate = invoiceTemplateOptions.find(t => t.id === templateId);
  
  // Get GST registration details - different thermal formats will be used based on this
  const gstRegistered = businessDetails?.gstRegistered || 'not_registered';
  const gstType = businessDetails?.gstType || 'regular';
  
  // Function to get the appropriate thermal format component
  const getThermalFormatComponent = () => {
    if (gstRegistered === 'registered') {
      if (gstType === 'composition') {
        return ThermalInvoiceFormatComposition;
      } else {
        return ThermalInvoiceFormatRegular;
      }
    } else {
      return ThermalInvoiceFormatUnregistered;
    }
  };
  
  // Get the appropriate thermal format component
  const ThermalComponent = getThermalFormatComponent();
  
  // Format client and business details for invoice formats
  const formatClientDetailsForTemplate = () => {
    return {
      name: invoiceData.client,
      address: invoiceData.clientDetails.address,
      city: '',
      state: invoiceData.clientDetails.state || '',
      pincode: '',
      phone: invoiceData.clientDetails.phone,
      email: invoiceData.clientDetails.email,
      gstin: invoiceData.clientDetails.gstin,
    };
  };
  
  const formatBusinessDetailsForTemplate = () => {
    return {
      name: businessDetails?.name || '',
      address: businessDetails?.address || '',
      city: businessDetails?.city || '',
      state: businessDetails?.state || '',
      pincode: businessDetails?.pincode || '',
      phone: businessDetails?.phone || '',
      email: businessDetails?.email || '',
      gstin: businessDetails?.gstin || '',
      bankName: businessDetails?.bankDetails?.bankName || '',
      bankBranch: businessDetails?.bankDetails?.branch || '',
      accountNumber: businessDetails?.bankDetails?.accountNumber || '',
      ifsc: businessDetails?.bankDetails?.ifsc || '',
      upiId: businessDetails?.bankDetails?.upiId || '',
      logoUrl: brandSettings?.logoUrl || businessDetails?.logoUrl || '',
      primaryColor: brandSettings?.primaryColor || '',
      gstRegistered: businessDetails?.gstRegistered || 'not_registered',
      gstType: businessDetails?.gstType || '',
    };
  };
  
  // Format items for templates
  const formatItemsForTemplate = () => {
    return invoiceData.items.map((item: any) => {
      // item.amount = base taxable value (before GST)
      // item.taxAmount = GST amount
      // item.total = item.amount + item.taxAmount
      // item.rate = per-unit price (before GST)
      const baseAmount = Number(item.amount) || 0;
      const taxAmt = Number(item.taxAmount) || 0;
      const totalAmt = Number(item.total) || (baseAmount + taxAmt);
      const qty = Number(item.quantity) || 1;
      const ratePerUnit = Number(item.rate) || (baseAmount / qty);

      return {
        productName: item.name || item.productName || '',
        description: item.description || '',
        quantity: qty,
        unit: item.unit || 'Nos',
        hsn: item.hsnCode || item.hsn || '',
        gstRate: Number(item.gstRate) || 0,
        taxableValue: baseAmount,       // pre-tax total for this line
        taxAmount: taxAmt,              // GST amount for this line
        total: totalAmt,               // final inclusive total
        rate: ratePerUnit,             // per-unit pre-tax price
      };
    });
  };
  
  // Format invoice details for template
  const formatInvoiceDetailsForTemplate = () => {
    return {
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate,
      discount: invoiceData.discount,
      termsAndConditions: invoiceData.termsAndConditions ? 
        invoiceData.termsAndConditions.split('\n') : [],
    };
  };
  
  // Handle print invoice
  const handlePrint = async () => {
    try {
      // Use custom callback if provided
      if (onPrint) {
        onPrint();
        return;
      }
      
      // Check if there's an iframe with the PDF and use it for printing
      const iframe = document.querySelector('iframe') as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.focus(); // Focus the iframe window
        iframe.contentWindow.print(); // Print the PDF from the iframe
        
        setNotification({
          open: true,
          message: 'Print dialog opened successfully',
          severity: 'success',
        });
        return;
      }
      
      // Fallback to using the InvoicePrinter utility if no iframe is found
      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;
      
      await InvoicePrinter.printInvoice(
        formatBusinessDetailsForTemplate(),
        formatClientDetailsForTemplate(),
        formatInvoiceDetailsForTemplate(),
        formatItemsForTemplate(),
        templateId // Always use template ID 1
      );
      
      setNotification({
        open: true,
        message: 'Print dialog opened successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error printing invoice:', error);
      setNotification({
        open: true,
        message: 'Error printing invoice. Please try again.',
        severity: 'error',
      });
    }
  };
  
  // Handle thermal print
  const handleThermalPrint = async () => {
    try {
      // Use custom callback if provided
      if (onThermalPrint) {
        onThermalPrint();
        return;
      }
      
      // Otherwise use the InvoicePrinter utility
      await InvoicePrinter.printThermalInvoice(
        formatBusinessDetailsForTemplate(),
        formatClientDetailsForTemplate(),
        {
          ...formatInvoiceDetailsForTemplate(),
          items: invoiceData.items,
        },
        thermalWidth
      );
      
      setNotification({
        open: true,
        message: 'Thermal print dialog opened successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error printing thermal invoice:', error);
      setNotification({
        open: true,
        message: 'Error printing thermal invoice. Please try again.',
        severity: 'error',
      });
    }
  };
  
  // Handle save as PDF
  const handleSavePdf = async () => {
    try {
      setGeneratingPdf(true);
      
      // Use custom callback if provided - this should save the invoice first
      if (onSavePdf) {
        onSavePdf();
        setGeneratingPdf(false);
        return;
      }
      
      // Try direct export approach with the invoice element
      if (invoiceRef.current) {
        try {
          // Make sure the invoice element has enough time to render completely
          setTimeout(async () => {
            try {
              // Ensure the element has an ID
              const elementId = invoiceRef.current?.id || 'invoice-for-export';
              if (invoiceRef.current) {
                invoiceRef.current.id = elementId;
              }
              
              // Use the exportInvoiceToPdf function with proper error handling
              await exportInvoiceToPdf(
                elementId, 
                `Invoice_${invoiceData.invoiceNumber}`
              );
              
              setNotification({
                open: true,
                message: 'PDF saved successfully',
                severity: 'success',
              });
            } catch (delayedError) {
              console.error('Error in delayed PDF export:', delayedError);
              // Fall back to the other method
              generatePdfWithPdfLib();
            } finally {
              setGeneratingPdf(false);
            }
          }, 500); // Give time for rendering
          
          return;
        } catch (exportError) {
          console.error('Error with direct PDF export:', exportError);
          // Continue to fallback method
        }
      }
      
      // Use the PDF lib approach if direct export fails
      generatePdfWithPdfLib();
    } catch (error) {
      console.error('Error saving PDF:', error);
      setNotification({
        open: true,
        message: 'Error saving PDF. Please try again.',
        severity: 'error',
      });
      setGeneratingPdf(false);
    }
  };
  
  // Helper function to use the PDF lib approach
  const generatePdfWithPdfLib = async () => {
    try {
      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;
      
      // Always use the PDF generator with AkMoInvoiceFormat
      const pdfBytes = await generateInvoicePDF(
        {
          ...invoiceData,
          businessDetails: formatBusinessDetailsForTemplate(),
          status: 'pending' // Ensure status is set to pending
        },
        businessDetails, // Full business details for the PDF generator
        templateId // Always use template ID 1
      );
      
      // Convert to Blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceData.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      setNotification({
        open: true,
        message: 'PDF saved successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error with PDF lib export:', error);
      setNotification({
        open: true,
        message: 'Error generating PDF. Please try again.',
        severity: 'error',
      });
    } finally {
      setGeneratingPdf(false);
    }
  };
  
  // Handle email invoice
  const handleOpenEmailDialog = () => {
    // Pre-fill email details
    setEmailTo(invoiceData.clientDetails.email || '');

    let subject = `Invoice ${invoiceData.invoiceNumber} from ${businessDetails?.name || 'Business'}`;
    let body = `Dear ${invoiceData.client},\n\nPlease find attached the invoice ${invoiceData.invoiceNumber} for your recent purchase.\n\nTotal Amount: ${formatCurrency(invoiceData.amount)}\nDue Date: ${invoiceData.dueDate}\n\nThank you for your business.\n\nRegards,\n${businessDetails?.name || ''}`;

    if (userInvoiceSettings) {
      const amountFormatted = formatCurrency(invoiceData.amount);
      const templateData = {
        customer_name: invoiceData.client,
        invoice_number: invoiceData.invoiceNumber,
        amount_due: amountFormatted,
        due_date: invoiceData.dueDate,
        business_name: businessDetails?.name || 'Business',
      };

      const parseTemplate = (tmpl: string) => {
        return tmpl
          .replace(/\{\{customer_name\}\}/g, templateData.customer_name)
          .replace(/\{clientName\}/g, templateData.customer_name)
          .replace(/\{\{invoice_number\}\}/g, templateData.invoice_number)
          .replace(/\{invoiceNumber\}/g, templateData.invoice_number)
          .replace(/\{\{amount_due\}\}/g, templateData.amount_due)
          .replace(/\{amount\}/g, templateData.amount_due)
          .replace(/\{\{due_date\}\}/g, templateData.due_date)
          .replace(/\{dueDate\}/g, templateData.due_date)
          .replace(/\{\{business_name\}\}/g, templateData.business_name)
          .replace(/\{businessName\}/g, templateData.business_name);
      };

      if (userInvoiceSettings.emailSubject) {
        subject = parseTemplate(userInvoiceSettings.emailSubject);
      }
      if (userInvoiceSettings.emailBody) {
        body = parseTemplate(userInvoiceSettings.emailBody);
      }
    }

    setEmailSubject(subject);
    setEmailBody(body);
    setEmailDialogOpen(true);
  };
  
  // Handle send email
  const handleSendEmail = async () => {
    setSendingEmail(true);
    
    try {
      // Use custom callback if provided
      if (onEmail) {
        await onEmail();
        setEmailDialogOpen(false);
        setSendingEmail(false);
        return;
      }
      
      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;
      
      // First try to generate PDF from the exact DOM (Zoho style A4)
      let pdfBytes: Uint8Array | null = null;
      try {
        const result = await exportInvoiceToPdf('invoice-preview', `Invoice_${invoiceData.invoiceNumber}`, true);
        if (result instanceof Uint8Array) {
          pdfBytes = result;
        }
      } catch (err) {
        console.error('Failed to get PDF from DOM export, falling back to pdf-lib', err);
      }

      // Fallback if the DOM export fails
      if (!pdfBytes) {
        pdfBytes = await generateInvoicePDF(
          {
            ...invoiceData,
            businessDetails: formatBusinessDetailsForTemplate(),
          },
          businessDetails,
          templateId
        );
      }
      
      // Send email with PDF attachment
      await EmailService.sendInvoiceEmail(
        emailTo,
        emailSubject,
        emailBody.replace(/\n/g, '<br/>'), // Convert newlines to HTML breaks
        pdfBytes,
        `Invoice_${invoiceData.invoiceNumber}.pdf`,
        userSmtpSettings
      );
      
      // Close dialog after sending
      setEmailDialogOpen(false);
      
      setNotification({
        open: true,
        message: 'Email sent successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setNotification({
        open: true,
        message: 'Error sending email. Opened email client as fallback.',
        severity: 'warning',
      });
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Handle create next invoice
  const handleCreateNextInvoice = () => {
    if (onCreateNextInvoice) {
      onCreateNextInvoice();
    }
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };
  
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return '#4caf50';
      case 'pending':
        return '#ff9800';
      case 'overdue':
        return '#f44336';
      case 'draft':
      default:
        return '#9e9e9e';
    }
  };
  
  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Payment Pending';
      case 'overdue':
        return 'Overdue';
      case 'draft':
      default:
        return 'Draft';
    }
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Action Buttons — hidden when embedded inline */}
      {!hideActionButtons && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={(e) => setPrintMenuAnchorEl(e.currentTarget)}
          >
            Print Invoice
          </Button>
          <Menu
            anchorEl={printMenuAnchorEl}
            open={Boolean(printMenuAnchorEl)}
            onClose={() => setPrintMenuAnchorEl(null)}
          >
            <MenuItem onClick={() => { setPrintMenuAnchorEl(null); handlePrint(); }}>
              A4 Standard (Zoho Style)
            </MenuItem>
            <MenuItem onClick={() => { setPrintMenuAnchorEl(null); setThermalWidth('3-inch'); setTimeout(handleThermalPrint, 100); }}>
              Thermal Print (3-inch)
            </MenuItem>
            <MenuItem onClick={() => { setPrintMenuAnchorEl(null); setThermalWidth('2-inch'); setTimeout(handleThermalPrint, 100); }}>
              Thermal Print (2-inch)
            </MenuItem>
          </Menu>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleSavePdf}
            disabled={generatingPdf}
          >
            {generatingPdf ? 'Generating PDF...' : 'Save as PDF'}
            {generatingPdf && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={handleOpenEmailDialog}
          >
            Email Invoice
          </Button>

          {onCreateNextInvoice && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateNextInvoice}
            >
              Create Next Invoice
            </Button>
          )}
        </Box>
      )}

      {/* Invoice Preview */}
      {hideActionButtons ? (
        // Bare A4 template (no Card wrapper) when embedded inline
        <Box
          ref={invoiceRef}
          id="invoice-preview"
          sx={{ backgroundColor: 'white', overflow: 'auto' }}
        >
          <ZohoAkMoA4Template
            businessDetails={formatBusinessDetailsForTemplate()}
            clientDetails={formatClientDetailsForTemplate()}
            invoiceDetails={formatInvoiceDetailsForTemplate()}
            items={formatItemsForTemplate()}
            primaryColor={brandSettings?.primaryColor || akmoTemplate?.colorScheme?.primary || "#3949AB"}
          />
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Invoice Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                ref={invoiceRef}
                id="invoice-preview"
                sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: 'white', overflow: 'auto' }}
              >
                <ZohoAkMoA4Template
                  businessDetails={formatBusinessDetailsForTemplate()}
                  clientDetails={formatClientDetailsForTemplate()}
                  invoiceDetails={formatInvoiceDetailsForTemplate()}
                  items={formatItemsForTemplate()}
                  primaryColor={brandSettings?.primaryColor || akmoTemplate?.colorScheme?.primary || "#3949AB"}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      
      {/* Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => !sendingEmail && setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Email Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="To"
              fullWidth
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              disabled={sendingEmail}
            />
            <TextField
              label="Subject"
              fullWidth
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              disabled={sendingEmail}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={6}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              disabled={sendingEmail}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailDialogOpen(false)} 
            color="inherit"
            disabled={sendingEmail}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendEmail} 
            color="primary"
            variant="contained"
            disabled={sendingEmail || !emailTo}
            startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoicePreviewStep; 